import { useTranslation } from 'react-i18next';

import type { AccessGroup } from '@/api/auth';
import type { ConsiderationRow } from '@/api/considerations';
import { CONSIDERATION_STATUSES } from '@/api/vocabularies';
import { Button } from '@/components/ui/Button';

import { AddCandidateButton } from './AddCandidateButton';
import {
  hasChosenFilters,
  type CandidateFilterInput,
  type CandidateFilters,
} from './candidateFilters';
import { SearchBox } from './SearchBox';
import { StatusFilter } from './StatusFilter';
import { VacancyFilter } from './VacancyFilter';

interface Props {
  filters: CandidateFilters;
  groups: AccessGroup[];
  /** The viewer, for the vacancy filter's viewer-keyed options entry (`queryKeys.roleOptions`). */
  viewerId: number;
  /** The rows on screen, for the one filter that has to build itself out of them. */
  rows: ConsiderationRow[];
  /**
   * A patch on the filter, which the screen merges and writes into the **address bar**. Every change
   * is therefore a history entry and the browser's back button undoes it, which is §6.3's reason for
   * putting the filter in the URL in the first place.
   */
  onChange: (patch: CandidateFilterInput) => void;
  /**
   * Opens §6.4's dialog. Forwarded verbatim to `AddCandidateButton`, whose own docblock records this
   * seam: the dialog is mounted **once** by the screen, so both places §6.3 puts the button (here and
   * the empty state) open the same one rather than each holding a form of its own.
   */
  onAdd: () => void;
}

/** §4's chip: the applied filter, stated. Not a `Button` — there is nothing to press on it. */
const chip =
  'inline-flex items-center rounded border border-rule bg-paper-sunken px-2 py-1 text-xs text-ink-700';

/**
 * §6.3's controls: *"Above the table: search by name, filters for vacancy and status, and Add
 * candidate (Recruiter, Admin)"* — plus the chip that says what is being filtered on, and the two
 * buttons that are **not** the same button.
 *
 * ⚠️ **Show all statuses and Clear filters go in opposite directions, and collapsing them would
 * break both.**
 *
 * | Button | Writes | Means |
 * |---|---|---|
 * | *Show all statuses* | all five statuses into the address | show me `HIRED` and `REJECTED` too |
 * | *Clear filters* | nothing at all into the address | back to §6.3's active-only default |
 *
 * The asymmetry is not an accident of the implementation: the default is expressed as the **absence**
 * of `status` (`candidateFilters.ts`), so *removing* the parameter re-applies the default, and only
 * naming every status survives a reload as "everything". *Show all statuses* is offered while the
 * default is in force; *Clear filters* while anything the viewer chose is.
 */
export function FilterBar({ filters, groups, rows, onChange, onAdd, viewerId }: Props) {
  const { t } = useTranslation(['candidates', 'components']);

  /** What the chip says: the default, everything, or the chosen statuses by name. */
  const appliedStatuses = () => {
    if (filters.statusIsDefault) return t('candidates:filters.activeOnly');
    if (filters.appliedStatus.length === CONSIDERATION_STATUSES.length) {
      return t('candidates:filters.allStatuses');
    }

    return t('candidates:filters.statusChosen', {
      statuses: filters.appliedStatus.map((status) => t(`components:status.${status}`)).join(', '),
    });
  };

  return (
    <div className="mt-6 flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <SearchBox value={filters.search} onCommit={(search) => onChange({ search })} />
        <StatusFilter applied={filters.appliedStatus} onChange={(status) => onChange({ status })} />
        <VacancyFilter
          viewerId={viewerId}
          rows={rows}
          selected={filters.vacancy}
          onChange={(vacancy) => onChange({ vacancy })}
        />
        <AddCandidateButton groups={groups} onClick={onAdd} className="ml-auto" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {/*
         * §6.3: *"a visible chip showing a filter is applied, because a filtered list that looks
         * unfiltered is how people conclude a candidate was lost."* There is **always** a status
         * filter on this screen — the default is one — so the chip is always here, and it reflects
         * the **applied** set rather than what the URL literally says: those two differ by exactly
         * the default, which is the case the sentence was written for.
         */}
        <span className={chip}>{appliedStatuses()}</span>

        {filters.statusIsDefault && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange({ status: CONSIDERATION_STATUSES })}
          >
            {t('candidates:filters.showAll')}
          </Button>
        )}

        {hasChosenFilters(filters) && <ClearFilters onChange={onChange} />}
      </div>
    </div>
  );
}

/**
 * *Clear filters* — back to §6.3's default. It is offered in two places (here and §8's filtered empty
 * state), which is why it is a component rather than a repeated `Button`.
 */
export function ClearFilters({ onChange }: Pick<Props, 'onChange'>) {
  const { t } = useTranslation('candidates');

  return (
    <Button
      variant="ghost"
      size="sm"
      // All three in one move: an empty `status` is what re-applies the default (**not** "no filter
      // at all"), and `candidateSearch` drops every empty value, so the address becomes `/candidates`.
      onClick={() => onChange({ status: [], vacancy: null, search: '' })}
    >
      {t('filters.clear')}
    </Button>
  );
}
